import { gql, useQuery } from '@apollo/client'
import { Link } from 'gatsby'
import { useState } from 'react'
import { formatDate } from '../../../helpers/string'
import { BulmaMessage } from '../../Bulma'
import { CertificateData } from './CertificateData'
import CertificateFiles from './CertificateFiles'
import CertificateNotes from './CertificateNotes'
import { Submit, TableSelectField } from './Fields'
import { paymentStatuses, recoveryCertificateStatuses } from './index'
import { OnSubmitParams, UpdateRecoveryCertificateForm } from './UpdateRecoveryCertificateForm'
import {
  RecoveryCertificateQuery,
  RecoveryCertificateQueryVariables,
  RecoveryCertificateQuery_recoveryCertificates,
} from './__generated__/RecoveryCertificateQuery'

const recoveryCertificatesQuery = gql`
  query RecoveryCertificateQuery($orderId: ID, $status: [RecoveryCertificateStatus], $unClaimed: Boolean) {
    recoveryCertificates(orderId: $orderId, status: $status, unClaimed: $unClaimed) {
      orderId
      UUID

      completingFor
      completingForFirstName
      completingForLastName
      completingForRelationshipToPatient
      firstName
      lastName
      dateOfBirth
      emailAddress
      telephoneNumber
      passportNumber

      travelToDestination
      travelToDestinationOtherDescription

      positiveCovidTestDate
      typeOfCovidTest
      typeOfCovidTestDescription
      covidSymptomsAroundTestDate
      lastTimeExperiencedSymptoms
      additionalInformation

      fileUrls

      notes
      status
      paymentStatus
      claimedBy
      claimedAt

      auditTrail {
        date
        user
        changes {
          key
          before {
            ... on AuditString {
              stringValue
            }
            ... on AuditArray {
              arrayValue
            }
          }
          after {
            ... on AuditString {
              stringValue
            }
            ... on AuditArray {
              arrayValue
            }
          }
        }
      }

      createdAt
    }
  }
`

export const RecoveryCertificate = ({
  orderId,
}: {
  orderId: RecoveryCertificateQuery_recoveryCertificates['orderId']
}) => {
  const { loading, data, refetch } = useQuery<RecoveryCertificateQuery, RecoveryCertificateQueryVariables>(
    recoveryCertificatesQuery,
    {
      variables: {
        orderId: orderId,
      },
    }
  )
  const [errorMessage, setErrorMessage] = useState<JSX.Element>()
  const [notificationMessage, setNotificationMessage] = useState<JSX.Element>()

  const recoveryCertificate = data?.recoveryCertificates[0]

  if (loading || !recoveryCertificate) {
    return <p>Loading...</p>
  }

  const edit = true

  const onSubmit = ({ result, action, status, error }: OnSubmitParams) => {
    setErrorMessage(undefined)
    setNotificationMessage(undefined)

    if (result) {
      refetch()
      setNotificationMessage(
        <>
          <p>
            <b>
              {action === 'EMAIL_CERTIFICATE_TO_CUSTOMER'
                ? 'Changes Have Been Saved, certificate has been emailed to the customer.'
                : 'Changes Have Been Saved Successfully'}
            </b>
          </p>
        </>
      )
    } else {
      setErrorMessage(
        <>
          <p>
            <b>An Error Occurred While Trying To Update The Order</b>
          </p>
          <br />
          <blockquote className="is-italic">{error.message}</blockquote>
        </>
      )
    }
  }

  return (
    <>
      <UpdateRecoveryCertificateForm
        recoveryCertificate={recoveryCertificate}
        refetchRecoveryCertificate={refetch}
        onSubmit={onSubmit}
      >
        {recoveryCertificate?.status === 'APPROVED' && (
          <BulmaMessage color="info">
            <p>
              If a customer requests changes to their recovery certificate you can make changes here and it
              automatically updates their certificate. No need to resend the email or anything else.
            </p>
          </BulmaMessage>
        )}

        {[
          'NEW',
          'AWAITING_RESPONSE_FROM_CUSTOMER',
          'REVIEWING_DOCUMENTATION',
          'DOCUMENTATION_COMPLETE',
          'FINAL_REVIEW',
        ].includes(recoveryCertificate?.status) && (
          <BulmaMessage color="warning">
            <p>
              Warning: This page should only be used for making changes AFTER the order is APPROVED, to make changes go
              to Recovery Certificates
            </p>
            <br />
            <p>
              <Link to={`/dashboard/recovery-certificates/${recoveryCertificate.orderId}`}>
                Go to Recovery Certificate
              </Link>
            </p>
          </BulmaMessage>
        )}

        {errorMessage && <BulmaMessage color="danger">{errorMessage}</BulmaMessage>}
        {notificationMessage && <BulmaMessage color="info">{notificationMessage}</BulmaMessage>}
        <div className="columns">
          <div className="column is-6">
            <div className="subtitle">Recovery Certificate: {orderId}</div>

            <article className="message">
              <div className="message-header">
                <p className="has-text-light">Status</p>
              </div>
              <div className="message-body p-0">
                <table className="table is-fullwidth is-striped is-size-7">
                  <tbody>
                    <tr>
                      <th>Order ID:</th>
                      <td>{recoveryCertificate?.orderId}</td>
                    </tr>
                    <tr>
                      <th>UUID:</th>
                      <td>{recoveryCertificate?.UUID}</td>
                    </tr>
                    <TableSelectField
                      label="Status"
                      value={recoveryCertificate?.status}
                      name="status"
                      options={recoveryCertificateStatuses.map((status) => ({
                        label: friendlyConstant(status),
                        value: status,
                      }))}
                      edit={edit}
                    />
                    <TableSelectField
                      label="Payment Status"
                      value={recoveryCertificate?.paymentStatus}
                      name="paymentStatus"
                      options={paymentStatuses.map((status) => ({
                        label: friendlyConstant(status),
                        value: status,
                      }))}
                      edit={edit}
                    />
                    {recoveryCertificate?.claimedBy && (
                      <tr>
                        <th>Completing For - First Name:</th>
                        <td>{recoveryCertificate?.claimedBy}</td>
                      </tr>
                    )}
                    {recoveryCertificate?.claimedAt && (
                      <tr>
                        <th>Completing For - First Name:</th>
                        <td>{recoveryCertificate?.claimedAt && formatDate(recoveryCertificate?.claimedAt)}</td>
                      </tr>
                    )}
                    <tr>
                      <th>Created:</th>
                      <td>{recoveryCertificate?.createdAt && formatDate(recoveryCertificate?.createdAt)}</td>
                    </tr>
                    <tr>
                      <th>Last Updated:</th>
                      <td>{recoveryCertificate?.updatedAt && formatDate(recoveryCertificate?.updatedAt)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </article>

            <CertificateNotes recoveryCertificate={recoveryCertificate} edit={edit} />

            <CertificateData recoveryCertificate={recoveryCertificate} edit={edit} />

            <CertificateFiles recoveryCertificate={recoveryCertificate} edit={edit} />

            {errorMessage && <BulmaMessage color="danger">{errorMessage}</BulmaMessage>}
            {notificationMessage && <BulmaMessage color="info">{notificationMessage}</BulmaMessage>}

            {edit && (
              <div className="has-text-centered">
                <Submit name="action" value="UPDATE" className="is-info is-size-6">
                  Save Changes
                </Submit>
              </div>
            )}
          </div>
          <div className="column is-6">
            <div className="subtitle">Actions</div>
            <article className="message">
              <div className="message-header">
                <p className="has-text-light">Actions</p>
              </div>
              <div className="message-body p-0">
                <BulmaMessage color="info" className="m-0">
                  <p>Every action also saves any changes made on this page</p>
                </BulmaMessage>
                <div className="p-4">
                  <Submit className="is-info is-fullwidth" name="action" value="UPDATE">
                    Save Changes
                  </Submit>
                  <Submit className="is-primary is-fullwidth mt-4" name="action" value="PREVIEW">
                    Preview Recovery Certificate
                  </Submit>
                  {recoveryCertificate?.status === 'APPROVED' && (
                    <Submit className="is-danger is-fullwidth mt-4" name="action" value="EMAIL_CERTIFICATE_TO_CUSTOMER">
                      Resend Email with Link to Certificate
                    </Submit>
                  )}
                </div>
              </div>
            </article>

            <div className="subtitle">Audit Trail</div>
            {[]
              .concat(recoveryCertificate?.auditTrail)
              .reverse()
              .map((item, key) => (
                <article className="message" key={key}>
                  <div className="message-header">
                    <p className="has-text-light">
                      {item?.date && formatDate(item.date)} - {item?.user}
                    </p>
                  </div>
                  <div className="message-body p-0">
                    {item?.changes?.length > 0 && (
                      <table className="table is-fullwidth is-striped is-size-7">
                        <thead>
                          <tr className="has-background-light">
                            <th style={{ width: '33%' }}>Field</th>
                            <th style={{ width: '33%' }}>Before</th>
                            <th style={{ width: '33%' }}>After</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item?.changes
                            .filter(
                              (change, key) =>
                                change.key !== 'Email' &&
                                (change.before?.stringValue ||
                                  change.before?.arrayValue ||
                                  change.after?.stringValue ||
                                  change.after?.arrayValue)
                            )
                            .map((change) => (
                              <tr>
                                <td>
                                  <b>{capSplit(change.key)}</b>
                                </td>
                                <td className="has-background-danger-light">{parseAuditValue(change.before)}</td>
                                <td className="has-background-success-light">{parseAuditValue(change.after)}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </article>
              ))}
          </div>
        </div>
      </UpdateRecoveryCertificateForm>
    </>
  )
}

const parseAuditValue = (value) =>
  value?.stringValue ? (
    value?.stringValue
  ) : (
    <ul>
      {value?.arrayValue?.map((current) => (
        <li>{current.split('/').pop()}</li>
      ))}
    </ul>
  )

export const issue = (
  status: RecoveryCertificateQuery_recoveryCertificates['status'],
  paymentStatus: RecoveryCertificateQuery_recoveryCertificates['paymentStatus']
) => {
  switch (paymentStatus) {
    case 'UNPAID':
    case 'CANCELLED_DURING_CHECKOUT':
    case 'PAYMENT_FAILED':
      return 'Recovery Certificate requires payment'
    case 'REFUNDED':
      return 'Recovery Certificate has been refunded'
  }

  switch (status) {
    case 'NEW':
    case 'REVIEWING_DOCUMENTATION':
      return 'Recovery Certificate requires verification'
    case 'AWAITING_RESPONSE_FROM_CUSTOMER':
      return 'Documentation incomplete, awaiting response from customer'
    case 'DOCUMENTATION_COMPLETE':
    case 'FINAL_REVIEW':
      return 'Recovery certificate requires final review'
    case 'CANCELLED':
      return 'Order was cancelled'
    case 'APPROVED':
      return 'Recovery certificate confirmed'
  }
}

export const friendlyConstant = (status) =>
  status
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase())

const capSplit = (str) =>
  str.replace(/(^[a-z]+)|[0-9]+|[A-Z][a-z]+|[A-Z]+(?=[A-Z][a-z]|[0-9])/g, (match, first) => {
    if (first) match = match[0].toUpperCase() + match.substr(1)
    return match + ' '
  })

export default RecoveryCertificate
